import { z } from 'zod';

const localPath = z.string().regex(/^[A-Za-z0-9_./-]+$/)
  .refine(path => !path.split('/').some(part => !part || part === '.' || part === '..'));
const stem = z.string().regex(/^[A-Za-z0-9_-][A-Za-z0-9_.-]*$/);

/** Validate only the structural fields needed to resolve package dependencies;
 * preserve bake-owned material parameters and future sidecar fields. */
export const CharacterMaterialsSidecarSchema = z.object({
  texture_format: z.enum(['png', 'webp', 'ktx2']).optional(),
  browser_texture_format: z.enum(['png', 'webp', 'ktx2']).optional(),
  materials: z.array(z.object({
    material_name: z.string().trim().min(1),
    kind: z.string().optional(),
    face_slot: z.string().optional(),
    textures: z.record(localPath).optional(),
    params: z.object({ alpha_stem: stem.optional(), tangent_stem: stem.optional() }).passthrough().optional(),
  }).passthrough()),
}).passthrough().superRefine((sidecar, ctx) => {
  const names = new Set<string>();
  for (const material of sidecar.materials) {
    if (names.has(material.material_name)) ctx.addIssue({ code: 'custom', message: `Duplicate material ${material.material_name}` });
    names.add(material.material_name);
  }
});

/** New uploads have root models; legacy backfills can retain nested ones. */
export const characterMaterialsFile = (modelPath: string) =>
  modelPath.slice(0, modelPath.lastIndexOf('/') + 1) + 'mh_materials.json';

/** Require every declared texture, including paths the renderers derive from
 * atlas stems and the browser's KTX2 substitution. No storage/network access. */
export function assertCharacterMaterialPackage(raw: unknown, paths: readonly string[], sidecarPath = 'mh_materials.json') {
  const sidecar = CharacterMaterialsSidecarSchema.parse(raw);
  const directory = sidecarPath.slice(0, sidecarPath.lastIndexOf('/') + 1);
  const inventory = new Set(paths);
  const required = new Set<string>();
  const browserPath = (path: string) => sidecar.browser_texture_format === 'ktx2'
    ? path.replace(/\.(png|webp)$/i, '.ktx2') : path;
  const requireTexture = (path: string) => {
    required.add(directory + path);
    required.add(directory + browserPath(path));
  };
  const extension = sidecar.texture_format ?? 'png';
  for (const material of sidecar.materials) {
    for (const path of Object.values(material.textures ?? {})) requireTexture(path);
    const alpha = material.params?.alpha_stem;
    if (alpha) {
      requireTexture(`textures/${alpha}.${extension}`);
      // Native image decoding uses the PNG/WebP companion even for full-KTX2 bakes.
      required.add(`${directory}textures/${alpha}.${extension === 'webp' ? 'webp' : 'png'}`);
    }
    const tangent = material.params?.tangent_stem;
    if (tangent) requireTexture(`textures/${tangent}.${extension}`);
    else if (alpha?.endsWith('_Attribute')) {
      // The browser probes convention-derived tangents, but older bakes deliberately
      // omit them. An explicitly named tangent above is never optional.
      const inferred = `textures/${alpha.replace(/_Attribute$/, '_Tangent')}.${extension}`;
      if (inventory.has(directory + inferred)) requireTexture(inferred);
    }
  }
  const missing = [...required].filter(path => !inventory.has(path)).sort();
  if (missing.length) throw new Error(`Material sidecar references texture files missing from the package: ${missing.join(', ')}`);
  return sidecar;
}
